import {
  useCallback,
  useEffect,
  useState } from 'react';
import {
  AlertIcon,
  Badge,
  Empty,
  Field,
  Grid,
  Note,
  Panel,
  RefreshIcon,
  ShieldIcon,
  Spinner,
  StateBadge,
  Stat,
  TableWrap,
  decimals,
  shortId,
} from '@wyocspay/ui';
import { badgeLabel, dateTime, money, relative } from './format.js';
import { StatusEditor } from './StatusEditor.js';
import { ApiError, api } from './api.js';
import { buildCommand, requireIdentity, signCommand } from './device.js';
import type { Signer } from './oversight.js';

/**
 * One payment, with everything the platform can do to it.
 *
 * The refund control here goes through the signed-command path and is
 * dual-controlled, so pressing the button does not refund anything - it queues
 * the action for a second administrator. The UI says so rather than looking
 * like it failed.
 */
/**
 * What this specific charge cost, read from Stripe.
 *
 * Kept out of the header stats because the figures can be in a different
 * currency: a EUR charge settling into a USD balance produces a net that is not
 * comparable with the gross without a rate, and putting them side by side under
 * one currency symbol would read as an error of several thousand.
 */
function FeeBreakdown({ paymentId }: { paymentId: string }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.paymentFees>> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    void api
      .paymentFees(paymentId)
      .then((d) => live && setData(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [paymentId]);

  if (failed) return null;
  if (!data) return null;

  if (!data.authoritative) {
    return (
      <Panel title="Разбивка на таксите">
        <Empty>
          Stripe още няма балансова транзакция за това плащане, така че реалните такси не могат да
          се прочетат. Показва се само таксата, която платформата е записала:{' '}
          {money(data.platformFeeCharged, data.currency)}.
        </Empty>
      </Panel>
    );
  }

  const settle = data.settlementCurrency ?? data.currency;

  return (
    <Panel title="Разбивка на таксите">
      <p style={{ marginTop: 0, fontSize: 13, color: 'var(--wc-muted)' }}>
        Прочетено от балансовата транзакция в Stripe - това е какво наистина е струвало, не
        изчисление.
      </p>

      {data.converted ? (
        <Note tone="warn" title="Плащането е конвертирано">
          Таксувано в {data.currency.toUpperCase()}, уредено в {settle.toUpperCase()}. Сумите
          по-долу са в {settle.toUpperCase()} и не се сравняват директно с брутната сума без курс.
        </Note>
      ) : null}

      <dl style={{ margin: '12px 0 0', display: 'grid', gap: 10 }}>
        <KV k="Бруто" v={money(data.amount, data.currency)} />
        <KV k="Такса на Stripe" v={money(data.stripeFee ?? 0, settle)} />
        <KV
          k="Такса на платформата"
          v={money(data.platformFee ?? data.platformFeeCharged, settle)}
        />
        <KV k="Нето за търговеца" v={<strong>{money(data.merchantNet ?? 0, settle)}</strong>} />
      </dl>

      {data.breakdown.length > 0 ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--wc-faint)', marginBottom: 6 }}>
            Позиции от Stripe
          </div>
          {data.breakdown.map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
              <span>{d.description ?? d.type}</span>
              <span className="wc-num">{money(d.amount, settle)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

export function AdminPaymentDetail({
  id,
  signer,
  onBack,
  onOpenMerchant,
}: {
  id: string;
  signer: Signer;
  onBack: () => void;
  onOpenMerchant: (merchantId: string) => void;
}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.payment>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string; url?: string } | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState<'' | 'duplicate' | 'fraudulent' | 'requested_by_customer'>('');

  const load = useCallback(async (silent?: unknown) => {
    const quiet = silent === true;
    if (!quiet) setLoading(true);
    try {
      setData(await api.payment(id));
      if (!quiet) setError(null);
    } catch (err) {
      if (!quiet) setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live: keep the payment fresh (status, refunds, alerts) without a reload.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <Spinner label="Зареждане на плащането…" />;
  if (error) return <Note tone="danger">{error}</Note>;
  if (!data) return null;

  const p = data.payment;
  const remaining = p.amount - p.amount_refunded;
  const refundable = ['SUCCEEDED', 'PARTIALLY_REFUNDED', 'DISPUTED'].includes(p.status) && remaining > 0;
  const places = decimals(p.currency);

  async function refund(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const parsed = refundAmount.trim() ? Math.round(Number.parseFloat(refundAmount) * 10 ** places) : undefined;
      const identity = requireIdentity(signer.identity);
      const cmd = buildCommand({
        actorId: signer.actorId,
        deviceId: identity.deviceId,
        target: `payment:${id}`,
        transition: 'refund',
        previousVersion: 0,
        params: {
          ...(parsed ? { amount: parsed } : {}),
          ...(refundReason ? { reason: refundReason } : {}),
        },
      });
      await api.command(cmd, await signCommand(identity, cmd));
      setMsg({ tone: 'ok', text: 'Рефундът е подаден към Stripe. Плащането се обновява, когато Stripe го потвърди.' });
      setRefundOpen(false);
      await load();
    } catch (err) {
      // A held action is not a failure - say what actually happened.
      if (err instanceof ApiError && err.code === 'approval_required') {
        setMsg({
          tone: 'warn',
          text:
            'Рефундите изискват втори администратор. Заявката чака в "Чака второ одобрение" - все още нищо не е рефундирано.',
        });
        setRefundOpen(false);
      } else {
        setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  async function link(mode: 'renew' | 'reissue'): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.paymentLink(id, mode);
      setMsg(
        mode === 'renew'
          ? { tone: 'ok', text: `Линкът, който клиентът вече има, сега важи до ${dateTime(r.expires_at)}.` }
          : { tone: 'warn', text: `Нов линк, валиден до ${dateTime(r.expires_at)}.`, url: r.url ?? undefined },
      );
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function reconcile(): Promise<void> {
    setBusy(true);
    setMsg(null);
    try {
      const identity = requireIdentity(signer.identity);
      const cmd = buildCommand({
        actorId: signer.actorId,
        deviceId: identity.deviceId,
        target: `payment:${id}`,
        transition: 'reconcile',
        previousVersion: 0,
      });
      const r = await api.command(cmd, await signCommand(identity, cmd));
      setMsg({
        tone: r.new_version > 0 ? 'warn' : 'ok',
        text: r.new_version > 0
          ? `Реконсилиацията откри ${r.new_version} несъответствие(я). Вижте Сигнали.`
          : 'Проверено срещу Stripe - локалното състояние съвпада.',
      });
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={onBack}>
          ← Назад
        </button>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={reconcile}>
            <ShieldIcon size={13} /> Провери срещу Stripe
          </button>
          {refundable && !refundOpen ? (
            <button type="button" className="wc-btn wc-btn-sm wc-btn-danger" onClick={() => setRefundOpen(true)}>
              Рефунд
            </button>
          ) : null}
        </div>
      </div>

      {msg ? (
        <Note tone={msg.tone}>
          {msg.text}
          {msg.url ? (
            <pre className="wc-num" style={{ margin: '8px 0 0', fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
              {msg.url}
            </pre>
          ) : null}
        </Note>
      ) : null}

      {data.alerts.filter((a) => a.status === 'OPEN').length > 0 ? (
        <Note tone="danger" title="Това плащане има отворени сигнали">
          {data.alerts.filter((a) => a.status === 'OPEN').map((a) => (
            <div key={a.id} style={{ marginTop: 4 }}>
              <strong>{a.kind}</strong> - {a.detail}
            </div>
          ))}
        </Note>
      ) : null}

      {refundOpen ? (
        <Panel title="Рефунд на това плащане">
          <Note tone="warn" title="Това изисква втори администратор">
            Рефундите изнасят пари и не могат да бъдат отменени от наша страна, затова действието се задържа, докато друг администратор не го подпише. Няма да можете да одобрите собствената си заявка.
          </Note>
          <div style={{ marginTop: 14 }}>
            <Field label={`Amount (blank = full ${money(remaining, p.currency)})`}>
              <input
                className="wc-input wc-num"
                inputMode="decimal"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={(remaining / 10 ** places).toFixed(places)}
              />
            </Field>
            <Field label="Причина">
              <select className="wc-input" value={refundReason} onChange={(e) => setRefundReason(e.target.value as typeof refundReason)}>
                <option value="">Не е посочено</option>
                <option value="requested_by_customer">Заявено от клиента</option>
                <option value="duplicate">Дублирано плащане</option>
                <option value="fraudulent">Измама</option>
              </select>
            </Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="wc-btn wc-btn-danger" disabled={busy} onClick={refund}>
                <ShieldIcon size={13} /> Подпиши и изпрати за одобрение
              </button>
              <button type="button" className="wc-btn wc-btn-secondary" onClick={() => setRefundOpen(false)}>
                Отказ
              </button>
            </div>
          </div>
        </Panel>
      ) : null}

      <Grid cols={4}>
        <Stat label="Сума" value={money(p.amount, p.currency)} />
        <Stat
          label="Статус"
          value={<StatusEditor paymentId={id} state={p.status} onChanged={() => void load()} />}
          hint={p.paid_at ? `Потвърдено ${relative(p.paid_at)}` : 'Неуреден'}
        />
        <Stat label="Рефундирано" value={money(p.amount_refunded, p.currency)} hint={badgeLabel(p.refund_status)} accent="var(--wc-blue)" />
        <Stat label="Такса на платформата" value={money(p.application_fee, p.currency)} accent="var(--wc-grey)" />
      </Grid>

      <FeeBreakdown paymentId={p.id} />

      <Grid cols={2}>
        <Panel title="Търговец">
          <dl style={{ margin: 0, display: 'grid', gap: 9 }}>
            <KV k="Име" v={data.merchant.name} />
            <KV k="Имейл" v={data.merchant.email} />
            <KV k="Статус" v={<Badge tone={data.merchant.status === 'ACTIVE' ? 'ok' : 'pending'}>{badgeLabel(data.merchant.status)}</Badge>} />
            <KV k="Свързан профил" v={<span className="wc-num" style={{ fontSize: 12 }}>{data.merchant.stripeAccountId ?? '-'}</span>} />
          </dl>
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            style={{ marginTop: 12 }}
            onClick={() => onOpenMerchant(data.merchant.id)}
          >
            Отвори търговеца
          </button>
        </Panel>

        <Panel title="Референции в Stripe">
          <dl style={{ margin: 0, display: 'grid', gap: 9 }}>
            <KV k="Плащане" v={<span className="wc-num" style={{ fontSize: 12 }}>{p.id}</span>} />
            <KV k="PaymentIntent" v={<span className="wc-num" style={{ fontSize: 12 }}>{p.stripe_payment_intent_id ?? '-'}</span>} />
            <KV k="Референция на търговеца" v={p.merchant_reference ?? '-'} />
            <KV k="Създаден" v={dateTime(p.created_at)} />
            <KV k="Изтича" v={p.expires_at ? dateTime(p.expires_at) : '-'} />
          </dl>
        </Panel>
      </Grid>

      <Panel
        title="Линкове за плащане"
        padded={false}
        actions={
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="wc-btn wc-btn-sm" disabled={busy} onClick={() => link('renew')}>
              <RefreshIcon size={12} /> Поднови
            </button>
            <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={() => link('reissue')}>
              Нов линк
            </button>
          </div>
        }
      >
        {data.links.length === 0 ? (
          <Empty>Няма издадени линкове.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>Издаден</th><th>Изтича</th><th>Състояние</th><th /></tr></thead>
              <tbody>
                {data.links.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{relative(l.created_at)}</td>
                    <td style={{ fontSize: 13 }}>{dateTime(l.expires_at)}</td>
                    <td>
                      <Badge tone={l.revoked ? 'fail' : l.expired ? 'neutral' : 'ok'}>
                        {l.revoked ? 'ОТНЕТ' : l.expired ? 'ИЗТЕКЪЛ' : 'АКТИВЕН'}
                      </Badge>
                    </td>
                    <td className="wc-right">
                      {!l.revoked && !l.expired ? (
                        <button
                          type="button"
                          className="wc-btn wc-btn-sm wc-btn-secondary"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            await api.revokePaymentLink(id, l.id).catch(() => undefined);
                            await load();
                            setBusy(false);
                          }}
                        >
                          Отнеми
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
        <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--wc-faint)' }}>
          Подновяването удължава линка, който клиентът вече държи - не се връща URL, защото се пази само хешът му. Администратор може да поднови линк, но не може да създаде плащане; таксуването на клиент остава решение на търговеца.
        </div>
      </Panel>

      {data.refunds.length > 0 ? (
        <Panel title="Рефунди" padded={false}>
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>Рефунд</th><th>Статус</th><th>Причина</th><th>Заявено от</th><th className="wc-right">Сума</th></tr></thead>
              <tbody>
                {data.refunds.map((r) => (
                  <tr key={r.id}>
                    <td className="wc-num" style={{ fontSize: 12 }}>{r.id}</td>
                    <td><Badge tone={r.status === 'succeeded' ? 'ok' : r.status === 'failed' ? 'fail' : 'pending'}>{badgeLabel((r.status ?? '').toUpperCase())}</Badge></td>
                    <td style={{ fontSize: 13 }}>{r.reason ?? '-'}</td>
                    <td className="wc-num" style={{ fontSize: 12 }}>{r.requested_by ?? '-'}</td>
                    <td className="wc-right wc-num">{money(r.amount, r.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      ) : null}

      <Panel title="Опити" padded={false}>
        {data.attempts.length === 0 ? (
          <Empty>Няма започнат опит.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>#</th><th>Статус</th><th>Метод</th><th>Грешка</th><th>Плащане</th><th>Уредено</th></tr></thead>
              <tbody>
                {data.attempts.map((a) => (
                  <tr key={a.id}>
                    <td className="wc-num">{a.sequence}</td>
                    <td><StateBadge state={a.status} label={badgeLabel(a.status)} /></td>
                    <td>{a.payment_method_type ?? '-'}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
                      {a.decline_code ?? a.failure_code ?? '-'}
                      {a.failure_message ? <div>{a.failure_message}</div> : null}
                    </td>
                    <td className="wc-num" style={{ fontSize: 11.5 }}>{a.stripe_charge_id ? shortId(a.stripe_charge_id, 5) : '-'}</td>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{dateTime(a.settled_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>

      <Panel title="Одит на жизнения цикъл">
        <p style={{ marginTop: 0, fontSize: 13, color: 'var(--wc-muted)' }}>
          Само записите от <code>stripe_webhook</code> могат да отбележат пари като получени.
          Всичко останало в този списък е променило метаданни, не сетълмент.
        </p>
        <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {data.timeline.map((t, i) => (
            <li
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 12,
                padding: '8px 0',
                borderBottom: i === data.timeline.length - 1 ? 'none' : '1px solid var(--wc-border)',
                fontSize: 13,
              }}
            >
              <span className="wc-num" style={{ color: 'var(--wc-faint)', fontSize: 11.5 }}>{dateTime(t.at)}</span>
              <span>
                <strong>{t.action}</strong>{' '}
                <Badge tone={t.actor === 'stripe_webhook' ? 'ok' : t.actor === 'reconciler' ? 'info' : 'neutral'}>
                  {t.actor}
                </Badge>
                <pre
                  style={{
                    margin: '4px 0 0',
                    fontSize: 11,
                    color: 'var(--wc-muted)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'var(--wc-mono)',
                  }}
                >
                  {JSON.stringify(t.data)}
                </pre>
              </span>
            </li>
          ))}
        </ol>
      </Panel>

      {data.alerts.length > 0 ? (
        <Panel title="История на сигналите" padded={false}>
          <TableWrap>
            <table className="wc-table">
              <thead><tr><th>Тежест</th><th>Вид</th><th>Детайли</th><th>Статус</th><th>Подаден</th></tr></thead>
              <tbody>
                {data.alerts.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Badge tone={a.severity === 'critical' ? 'fail' : a.severity === 'warning' ? 'warn' : 'info'}>
                        <AlertIcon size={11} /> {a.severity.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="wc-num" style={{ fontSize: 12 }}>{a.kind}</td>
                    <td style={{ fontSize: 13 }}>{a.detail}</td>
                    <td><Badge tone={a.status === 'OPEN' ? 'warn' : 'neutral'}>{badgeLabel(a.status)}</Badge></td>
                    <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{relative(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      ) : null}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
      <dt style={{ fontSize: 13, color: 'var(--wc-muted)' }}>{k}</dt>
      <dd style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--wc-ink)' }}>{v}</dd>
    </div>
  );
}
